import {Component} from 'react';
import PropTypes from 'prop-types';
import {calculateDuration} from '@deliveriesondemand/dod-utils';

import {Control, ControlGroup} from '-/components/fields/Control';
import TextInput from '-/components/fields/TextInput';
import {NumberInput} from '-/components/fields/NumberInput';
import Address from '-/components/general/Address';
import CreditorService from '-/services/CreditorService';
import Button from '-/components/general/Button';
import {cloneDeep, isEmpty} from '-/util/lodash';
import Legend from '-/components/fields/Legend';

class PriorEmployment extends Component {
    static propTypes = {
        value: PropTypes.arrayOf(PropTypes.shape({
            position: PropTypes.string,
            priorEmployerName: PropTypes.string,
            priorEmployerAddress: PropTypes.shape({
                street1: PropTypes.string,
                street2: PropTypes.string,
                city: PropTypes.string,
                state: PropTypes.string,
                county: PropTypes.string,
                zip: PropTypes.string,
                formatted: PropTypes.string
            }),
            priorEmployerMonthsWorked: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string
            ]),
            priorEmployerYearsWorked: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string
            ])
        })),
        monthsNeeded: PropTypes.number,
        onChange: PropTypes.func
    };

    static defaultProps = {
        value: [],
        monthsNeeded: 0,
        onChange: () => {}
    };

    constructor(props) {
        super(props);

        this.onStateSet = this.onStateSet.bind(this);
        this.handleItemAdd = this.handleItemAdd.bind(this);

        this.state = {
            value: [],
            isModifying: false
        };
    }

    getErrors() {
        const {monthsNeeded} = this.props;
        const monthsSoFar = this.getMonthsSoFar();
        if (monthsSoFar < monthsNeeded) {
            const extraMonthsNeeded = monthsNeeded - monthsSoFar;
            const {months, years} = calculateDuration(0, extraMonthsNeeded);
            const yearLabel = `${years} more year${years > 1 ? 's' : ''}`;
            const monthLabel = `${months} more month${months > 1 ? 's' : ''}`;

            return [`Please provide ${years ? yearLabel : ''}${years && months ? ' and ' : ''}${months ? monthLabel : ''} of employment history`];
        } else {
            const {value} = this.state;
            for (let i = 0; i < value.length; i++) {
                const prior = value[i];
                const {position, priorEmployerName, priorEmployerAddress = {}, priorEmployerYearsWorked: yearsRaw, priorEmployerMonthsWorked: monthsRaw} = prior;
                const {street1} = priorEmployerAddress;
                const yearsAtPriorAddress = isNaN(yearsRaw) ? 0 : yearsRaw;
                const monthsAtPriorAddress = isNaN(monthsRaw) ? 0 : monthsRaw;


                if (!priorEmployerName || !position || !street1 || (!isFinite(yearsAtPriorAddress) && !isFinite(monthsAtPriorAddress))) {
                    return ['Please fill in every prior employment field'];
                }
            }
        }
        return [];
    }

    componentDidMount() {
        const {value} = this.props;

        this.setState({value}, this.onStateSet);
    }

    componentWillReceiveProps(nextProps) {
        this.setState({value: nextProps.value}, this.onStateSet);
    }

    onChange() {
        const {value} = this.state;
        const {onChange} = this.props;

        onChange(value);
    }

    handleFieldChange(i, key, name) {
        const {value} = this.state;
        value[i][key] = name;
        this.setState({value}, this.onChange);
    }

    onStateSet() {
        const {value: currentValue, isModifying = false} = this.state;
        const {priorEmploymentRequired} = this.props;
        const value = cloneDeep(currentValue);
        // ensure we always have at least one item
        if (value.length === 0 && priorEmploymentRequired) {
            value.push({
                position: '',
                priorEmployerName: '',
                priorEmployerAddress: {},
                priorEmployerMonthsWorked: '',
                priorEmployerYearsWorked: ''
            });
            this.setState({value});
        } else if (value.length && !priorEmploymentRequired && !isModifying) {
            let allEmpty = true;

            value.forEach(item => {
                const {position, priorEmployerName, priorEmployerAddress, priorEmployerMonthsWorked, priorEmployerYearsWorked} = item;

                if (priorEmployerName || position || !isEmpty(priorEmployerAddress) || priorEmployerYearsWorked || priorEmployerMonthsWorked) {
                    allEmpty = false;
                }
            });

            if (allEmpty) {
                this.setState({value: []});
            }
        }
    }

    handleItemDelete(i) {
        const {value: currentValue} = this.state;
        const value = cloneDeep(currentValue);
        const {priorEmploymentRequired} = this.props;

        if (i === 0 && priorEmploymentRequired) {
            // if prior employment is required, don't allow the last item to be removed;
            // replace it with a blank
            if (value.length === 1) {
                value.splice(0, 1, {
                    position: '',
                    priorEmployerName: '',
                    priorEmployerAddress: {},
                    priorEmployerMonthsWorked: '',
                    priorEmployerYearsWorked: ''
                });
                this.setState({value}, this.onChange);
                return;
            }
        }

        value.splice(i, 1);
        this.setState({value, isModifying: true}, this.onChange);
    }

    handleItemAdd() {
        const {value: currentValue} = this.state;
        const value = cloneDeep(currentValue);
        
        value.push({
            position: '',
            priorEmployerName: '',
            priorEmployerAddress: {},
            priorEmployerMonthsWorked: '',
            priorEmployerYearsWorked: ''
        });
        this.setState({value, isModifying: true}, () => {
            this.onChange();
            window.scrollTo(0, document.body.scrollHeight);
        });
    }

    getMonthsSoFar() {
        const {value} = this.state;

        return value.map(item => {
            const {priorEmployerMonthsWorked: months, priorEmployerYearsWorked: years} = item;
            const {months: calculatedMonths, years: calculatedYears} = calculateDuration(years, months);
            const finalMonths = isNaN(calculatedMonths) ? 0 : calculatedMonths;
            const finalYears = isNaN(calculatedYears) ? 0 : calculatedYears;

            return finalMonths + (finalYears * 12);
        }).reduce((total, item) => total + item, 0);
    }

    render() {
        const {value} = this.state;
        const {monthsNeeded, priorEmploymentRequired} = this.props;
        const monthsSoFar = this.getMonthsSoFar();
        const priorWorkLegend = `Where did you work before? ${!priorEmploymentRequired ? '(optional)' : ''}`;

        return (
            <div>
                <ControlGroup>
                    <Legend>{priorWorkLegend}</Legend>
                    {priorEmploymentRequired &&
                        <p className="info">We need at least {CreditorService.getYearsWorkHistoryRequired()} years of employment history.</p>
                    }
                </ControlGroup>
                {!value.length && !priorEmploymentRequired &&
                    <Button
                        btnStyle="add"
                        onClick={this.handleItemAdd}>
                        <i className="fa fa-plus" /> Add
                    </Button>
                }
                {value.map((item, i) => {
                    // show delete button if prior employment isn't required, or otherwise if there are more than 1 historical entries
                    const showDeleteBtn = !priorEmploymentRequired || (value.length === 1 ? false : i > 0);
                    // show add button if prior employment isn't required, or if we still have employment gaps
                    const showAddBtn = !priorEmploymentRequired || (monthsSoFar < monthsNeeded && i === value.length - 1);
                    return (
                        <ControlGroup labelWidth={200} key={i} className="test">
                            <Control>
                                <TextInput
                                    label="Prior Employer Name"
                                    placeholder="Company"
                                    value={item.priorEmployerName || ''}
                                    onChange={value => this.handleFieldChange(i, 'priorEmployerName', value)} />
                            </Control>
                            <Control>
                                <TextInput
                                    label="Position"
                                    placeholder="Title"
                                    value={item.position || ''}
                                    onChange={value => this.handleFieldChange(i, 'position', value)} />
                            </Control>
                            <Address
                                value={item.priorEmployerAddress}
                                onChange={value => this.handleFieldChange(i, 'priorEmployerAddress', value)} />
                            <Control>
                                <NumberInput
                                    value={item.priorEmployerYearsWorked}
                                    label="Years Worked"
                                    placeholder="X Years"
                                    onChange={value => this.handleFieldChange(i, 'priorEmployerYearsWorked', parseInt(value))} />
                            </Control>
                            <Control>
                                <NumberInput
                                    value={item.priorEmployerMonthsWorked}
                                    label="Months Worked"
                                    placeholder="X Months"
                                    onChange={value => this.handleFieldChange(i, 'priorEmployerMonthsWorked', parseInt(value))} />
                            </Control>
                            {showAddBtn ? (
                                <Button
                                    btnStyle="add"
                                    onClick={this.handleItemAdd}>
                                    <i className="fa fa-plus" /> Add
                                </Button>
                            ) : null}
                            {showDeleteBtn ? (
                                <Button
                                    btnStyle="delete"
                                    onClick={() => this.handleItemDelete(i)}>
                                    <i className="fa fa-close" /> Delete
                                </Button>
                            ) : null}
                        </ControlGroup>
                    );
                })}
            </div>
        );
    }
}

export default PriorEmployment;
